import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { useFeature } from '@growthbook/growthbook-react'
import { isNil } from 'lodash'

import cookie from 'lib/cookie'
import { useGetPopularKeywordsQuery } from 'store/api'
import { useGetGroupCategoriesQuery } from 'store/api/yay/group'
import { selectIsLoggedIn } from 'store/selectors'
import { useRedirectToApp, useSetUserProperties } from 'hooks'
import { SessionStorage } from 'lib/storage'
import {
  useChangeLanguageMutation,
  useGetCurrentUserQuery,
  useGetSettingQuery,
} from 'store/api/yay/user'
import {
  BrowserTracker,
  initTracker,
  snowplowEvent,
  trackPageView,
} from 'lib/analytics'

export const useWebsiteInitializer = () => {
  const location = useLocation()

  const { i18n } = useTranslation()
  const { set } = useSetUserProperties()

  const [tracker, setTracker] = useState<BrowserTracker>()
  const [isTrackerReady, setIsTrackerReady] = useState(false)

  const isLoggedIn = useSelector(selectIsLoggedIn)

  const {
    data: currentUser,
    isSuccess,
    refetch,
  } = useGetCurrentUserQuery(undefined, {
    skip: !Boolean(cookie.accessToken.get()) || !Boolean(cookie.userId.get()),
  })

  const popularKeywords = useGetPopularKeywordsQuery(i18n.language)

  const isSnowplowEnable = useFeature('yay-12638')

  // Redirect to app handler.
  useRedirectToApp(window?.gon?.referrer)

  useGetGroupCategoriesQuery({})

  useGetSettingQuery(null, {
    skip: !isLoggedIn,
  })

  const [changeLanguage] = useChangeLanguageMutation()

  useEffect(() => {
    const appVersion = window?.gon?.version
    const currentAppVersion = cookie.appVersion.get()

    // Update app version in cookie.
    if (appVersion !== currentAppVersion) {
      cookie.appVersion.set(appVersion)
    }
  }, [])

  useEffect(() => {
    if (isSnowplowEnable.on && !tracker) {
      setTracker(initTracker())
    }
  }, [isSnowplowEnable.on])

  useEffect(() => {
    if (tracker && (!currentUser?.id || isSuccess)) {
      set(currentUser).then(() => {
        setIsTrackerReady(true)

        const snowplowTemp = SessionStorage.snowplowTemp.get()

        if (
          snowplowTemp?.type === 'login' &&
          snowplowTemp?.id === currentUser?.id
        ) {
          SessionStorage.snowplowTemp.remove()
          snowplowEvent.login()
        }
      })
    }
  }, [isSuccess, currentUser?.id, tracker])

  useEffect(() => {
    if (
      !isSnowplowEnable.on ||
      !isTrackerReady ||
      (currentUser?.id && !isSuccess)
    )
      return

    trackPageView()
  }, [isTrackerReady, isSuccess, currentUser?.id, location.pathname])

  useEffect(() => {
    if (isLoggedIn) {
      refetch()
    } else {
      popularKeywords.refetch()
    }
  }, [isLoggedIn, currentUser?.id])

  useEffect(() => {
    if (isSuccess) {
      popularKeywords.refetch()
    }
  }, [isSuccess])

  useEffect(() => {
    setIsTrackerReady(false)
  }, [currentUser?.id])

  // https://jira.nanameue.jp/browse/YAY-15295
  // Need this for mission tracking for `open_app`.
  useEffect(() => {
    if (isLoggedIn && !isNil(currentUser?.id)) {
      changeLanguage(i18n?.language)
    }
  }, [isLoggedIn, currentUser?.id])
}
